import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";
import api from "../../lib/api";
import { Label } from "../../components/common";
import { Panel, Distribution, SsiTile, Touchpoints, Themes, Recovery, Mini, fmtV, weekLabel, PdfButton } from "../../components/scorecard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import PeriodFilter from "../../components/PeriodFilter";
import ResponsesSummary from "../../components/ResponsesSummary";
import { feedbackHref } from "../../lib/feedbackLink";


function DaypartChart({ rows }) {
  return (
    <div style={{ width: "100%", height: 220 }} data-testid="outlet-daypart-chart">
      <ResponsiveContainer>
        <BarChart data={rows} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
          <XAxis dataKey="daypart" tick={{ fontSize: 11, fill: "#94A3B8" }} />
          <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} width={40} />
          <YAxis yAxisId="r" orientation="right" domain={[0, 5]} tick={{ fontSize: 10, fill: "#94A3B8" }} width={30} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="l" dataKey="ratings" name="Ratings" fill="#C7D2FE" radius={[4, 4, 0, 0]} />
          <Bar yAxisId="r" dataKey="avg_rating" name="Avg rating" fill="#4338CA" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RepeatTrend({ rows }) {
  if (!rows.length) return <p className="text-sm text-slate-400">No returning-customer data yet.</p>;
  return (
    <div style={{ width: "100%", height: 200 }} data-testid="outlet-repeat-chart">
      <ResponsiveContainer>
        <LineChart data={rows.map((r) => ({ ...r, label: weekLabel(r.week) }))} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} />
          <YAxis domain={[1, 5]} tick={{ fontSize: 10, fill: "#94A3B8" }} width={30} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line dataKey="repeat_avg" name="Returning customers" stroke="#4338CA" strokeWidth={2} connectNulls />
          <Line dataKey="new_avg" name="First-time customers" stroke="#94A3B8" strokeWidth={2} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function OutletScorecard() {
  const [locations, setLocations] = useState([]);
  const [searchParams] = useSearchParams();
  const [loc, setLoc] = useState(() => searchParams.get("location_id") || "all");
  const [days, setDays] = useState(() => searchParams.get("days") || "7");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setD(null);
    const q = `days=${days}${loc !== "all" ? `&location_id=${loc}` : ""}`;
    api.get(`/scorecards/outlet?${q}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [loc, days]);
  const sc = d?.scan_conversion;
  const navigate = useNavigate();
  const go = (extra) => navigate(feedbackHref({ location_id: loc !== "all" ? loc : "", days, ...extra }));

  return (
    <div className="space-y-6" data-testid="outlet-scorecard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Label>Operations</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Outlet &amp; Shift Scorecard</h1></div>
        <div className="flex flex-wrap gap-2">
          <Select value={loc} onValueChange={setLoc}>
            <SelectTrigger className="w-48" data-testid="outlet-location-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All my locations</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
          <PeriodFilter value={days} onChange={setDays} testId="outlet-period" />
          <PdfButton path={`/scorecards/outlet.pdf?days=${days}${loc !== "all" ? `&location_id=${loc}` : ""}`}
            filename={`servantage_outlet_scorecard_${days}d.pdf`} testId="outlet-pdf-btn" />
        </div>
      </div>
      {!d ? <p className="py-16 text-center text-sm text-slate-400">Loading…</p> : d.error ? <p className="py-16 text-center text-sm text-slate-400">Could not load the scorecard.</p> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SsiTile d={d} testId="outlet-ssi" sub={`${d.ratings} ratings`} />
            <Mini label="Ratings per operating hour" value={fmtV(d.ratings_per_operating_hour)} sub={d.ratings_per_operating_hour == null ? "Add opening hours on Locations" : null} />
            <Mini label="Scan conversion" value={fmtV(sc.rate, "%")} sub={`NFC ${fmtV(sc.nfc.rate, "%")} · QR ${fmtV(sc.qr.rate, "%")} (${sc.scans} scans)`} />
            <Mini label="Ratings per 100 transactions" value="—" sub="Needs POS data" />
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <Panel title="Ratings & average by daypart" testId="outlet-dayparts">
              <DaypartChart rows={d.dayparts} />
              <div className="mt-3 overflow-x-auto sv-scroll">
                <table className="w-full min-w-[480px] text-sm" data-testid="outlet-daypart-table">
                  <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400"><th className="py-1.5">Daypart</th><th>Ratings</th><th>Avg</th><th>Index</th><th>4–5★</th><th>1–2★</th></tr></thead>
                  <tbody>{d.dayparts.map((p) => (
                    <tr key={p.daypart} className="border-b border-slate-100">
                      <td className="py-1.5 font-semibold text-slate-700">{p.daypart} <span className="text-xs font-normal text-slate-400">{p.hours}</span></td>
                      <td>{p.ratings}</td><td>{fmtV(p.avg_rating)}</td><td>{fmtV(p.ssi)}</td>
                      <td>{fmtV(p.positive_rate, "%")}</td><td>{p.ratings ? `${Math.round((p.distribution[1] + p.distribution[2]) * 10) / 10}%` : "—"}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            </Panel>
            <Panel title="Rating distribution"><Distribution dist={d.distribution} testId="outlet-dist" onPick={(s) => go({ rating: s })} />
              <div className="mt-4"><Label>Touchpoint scores</Label><div className="mt-2"><Touchpoints tp={d.touchpoints} testId="outlet-touchpoints" onPick={(k) => go({ dimension: k })} /></div></div>
            </Panel>
            <Panel title="Busiest hours & their scores" testId="outlet-busiest">
              <ul className="space-y-1.5 text-sm">{d.busiest.map((b) => (
                <li key={b.hour} className="flex justify-between"><span className="font-semibold text-slate-700">{b.label}</span><span className="text-slate-500">{b.ratings} ratings · avg {b.avg_rating}</span></li>))}
              </ul>
            </Panel>
            <Panel title="Top service friction (from comments on 1–3★)" testId="outlet-friction"><Themes items={d.friction} testId="outlet-friction-list" empty="No friction themes in this period." onPick={(t) => go({ theme: t })} /></Panel>
            <Panel title="Manager response to low ratings" testId="outlet-recovery"><Recovery r={d.recovery} testId="outlet-recovery-stats" /></Panel>
            <Panel title="Returning vs first-time customer ratings" testId="outlet-repeat"><RepeatTrend rows={d.repeat_trend} /></Panel>
          </div>
          <ResponsesSummary locationId={loc !== "all" ? loc : undefined} days={days} testId="outlet-responses-summary" />
        </>
      )}
    </div>
  );
}
